import { inject, Injectable } from '@angular/core'

import { firstValueFrom, takeUntil } from 'rxjs'

import { AuditLog, DataQueryProps } from '@ti/contracts'
import { FilterData, PreDefinedRange } from '@ti/core/common/contracts'
import { DataGridColumnType } from '@ti/core/contracts/data-grid'
import { ListModel } from '@ti/core/models/list.model'
import { ListModelConfig } from '@ti/core/models/types'
import { injectRouteReattached$ } from '@ti/core/ui-kit/layout/utils'

import { LogsGridData } from '../contracts'
import { AuditLogGridDataResolver } from './grid-data.resolver'

@Injectable()
export class AuditLogsListModel extends ListModel<LogsGridData> {
  protected readonly dataResolver = inject(AuditLogGridDataResolver)
  protected readonly routeReattached$ = injectRouteReattached$()

  protected readonly categoryLabels$ = this.languageService.massTranslate$(
    Object.fromEntries(
      this.dataResolver.SUPPORTED_CATEGORIES.map((name) => [name, `audit-log.category.${name}`]),
    ),
  )

  protected readonly commonLabels$ = this.languageService.massTranslate$({
    category: 'audit-log.grid.category',
    systemUser: 'audit-log.common.system',
    error: 'audit-log.common.error',
  })

  public constructor() {
    super()

    this.languageService.current$.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      this.refresh()
      await this.updateFilterOptions()
    })
  }

  protected override async init() {
    await super.init()
    this.routeReattached$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refresh())
  }

  protected async loadPage(props: DataQueryProps) {
    const [labels, data] = await Promise.all([
      firstValueFrom(this.categoryLabels$),
      this.dataResolver.loadPage(props),
    ])
    return this.prepareGridData(data, labels)
  }

  protected async loadCount() {
    return 0 // Count is not used
  }

  protected config(): ListModelConfig {
    const fieldTranslationKeys = this.dataResolver.getGridTranslationKeys()
    return {
      name: 'LogsListModel',
      retainCacheUrl: ['settings/vehicles'],
      loadFilterOptions: () => this.loadFilterOptions(),
      defaultOrderColumn: 'actionDate',
      defaultOrderDirection: 'DESC',
      dateRangeColumn: 'actionDate',
      refreshOnLangChange: true,
      defaultDateRange: PreDefinedRange.Today,
      useTitleForExportFileName: false,
      exportReport: (format, fileName, props) => {
        return firstValueFrom(this.dataResolver.export$(format, fileName, props))
      },
      gridColumns: [
        {
          field: 'userName',
          label: fieldTranslationKeys.userName,
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'action',
          label: fieldTranslationKeys.action,
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'actionDate',
          label: fieldTranslationKeys.actionDate,
          type: DataGridColumnType.Date,
          sortable: true,
        },
        {
          field: 'category',
          label: fieldTranslationKeys.category,
          type: DataGridColumnType.Template,
          sortable: true,
        },
      ],
    }
  }

  protected async loadFilterOptions() {
    const [categoryLabels, labels, users] = await Promise.all([
      firstValueFrom(this.categoryLabels$),
      firstValueFrom(this.commonLabels$),
      this.dataResolver.getAllUsers(),
    ])

    const filters: FilterData[] = [
      {
        key: 'userId',
        type: 'list-multiselect',
        title: categoryLabels.users,
        options: [
          { label: labels.systemUser, value: this.dataResolver.getSystemUserFilterValue() },
          ...users.map((user) => ({ label: user.name, value: user.id })),
        ],
        searchable: true,
      },
      {
        key: 'category',
        type: 'list-multiselect',
        title: labels.category,
        options: this.dataResolver.SUPPORTED_CATEGORIES.map((categoryId) => ({
          label: categoryLabels[categoryId],
          value: categoryId,
        })),
        searchable: false,
      },
    ]

    return filters
  }

  protected async prepareGridData(data: AuditLog[], labels: Record<string, string>) {
    return Promise.all(
      data.map(
        async (row) =>
          ({
            ...row,
            actionKey: await this.dataResolver.getActionKey(row),
            actionData: await this.dataResolver.getActionData(row),
            categoryName: labels[row.category] || row.category,
            userName: row.userId || row.userName ? row.userName || '' : labels.systemUser,
          }) as LogsGridData,
      ),
    )
  }
}
